import { InstancedMesh, InstancedBufferAttribute, Matrix4, Vector3, DynamicDrawUsage } from 'three';
import { hexToLinear } from '../../core/color';
import { createMeshBuilder, type MeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import { SURFACE, edgeIdFor } from '../mesh/attributes';
import type { BuildContext } from '../types';
import type { LifeEntrance, LifePose } from './types';
import type { Vec3 } from '../../geo/types';

/* The back of a raised plank touches the solid leaf and is never exposed, including while the
   door swings. Omitting that buried face preserves every outside surface and its winding. */
function raisedDetail(builder: MeshBuilder, style: VertexStyle, min: Vec3, max: Vec3): void {
  const frame = { origin: [0, 0, 0] as const, yaw: 0 };
  const [x0, y0, z0] = min,
    [x1, y1, z1] = max;
  builder.box(style, frame, min, max, { top: true, bottom: true, sides: false });
  for (const face of [
    [
      [x1, y1, z0],
      [x1, y1, z1],
      [x1, y0, z1],
      [x1, y0, z0],
    ],
    [
      [x1, y1, z1],
      [x0, y1, z1],
      [x0, y0, z1],
      [x1, y0, z1],
    ],
    [
      [x0, y1, z1],
      [x0, y1, z0],
      [x0, y0, z0],
      [x0, y0, z1],
    ],
  ] as const)
    builder.quad(style, frame, face);
}

/** Real door leaves replace their static panes; dark shallow recesses conceal interior stays. */
export function buildLifeDoors(ctx: BuildContext, entrances: readonly LifeEntrance[]) {
  const leaf = createMeshBuilder(),
    surround = createMeshBuilder();
  const frame = { origin: [0, 0, 0] as const, yaw: 0 };
  const style = (color: readonly [number, number, number]) => ({
    color,
    surface: SURFACE.wood,
    edgeId: 1740,
    strokeAngle: 0,
    extent: [1, 2] as const,
  });
  leaf.box(style(hexToLinear('#564329')), frame, [0, 0, -0.035], [1, 1, 0.035]);
  for (const y of [0.2, 0.8])
    raisedDetail(leaf, style(hexToLinear('#302a22')), [0.02, y, 0.035], [0.85, y + 0.023, 0.055]);
  for (let x = 0.16; x < 1; x += 0.16)
    raisedDetail(leaf, style(hexToLinear('#443421')), [x, 0.02, 0.035], [x + 0.012, 0.98, 0.045]);
  surround.box(style(hexToLinear('#080a08')), frame, [-0.07, 0, -1.25], [1.07, 1.05, -0.85]);
  for (const x of [-0.04, 1.015])
    surround.box(style(hexToLinear('#aaa17b')), frame, [x, 0, -0.01], [x + 0.025, 1.02, 0.045]);
  surround.box(style(hexToLinear('#aaa17b')), frame, [-0.04, 1, -0.01], [1.04, 1.025, 0.045]);
  /* Normal door details include this threshold; preserve it when replacing their leaf. */
  surround.box(style(hexToLinear('#8c8365')), frame, [-0.08, 0, 0], [1.08, 0.035, 0.08]);
  const material = ctx.materials.painted({ instanced: true });
  const meshes = [leaf, surround].map((b, i) => {
    const geometry = b.finish();
    geometry.setAttribute(
      'aInstanceEdgeId',
      new InstancedBufferAttribute(
        Float32Array.from(entrances.map((_, j) => edgeIdFor(1740 + j))),
        1
      )
    );
    geometry.setAttribute(
      'aPainterScale',
      new InstancedBufferAttribute(Float32Array.from(entrances.flatMap(() => [1, 1])), 2)
    );
    const mesh = new InstancedMesh(geometry, material, entrances.length);
    mesh.name = ['life-door-leaves', 'life-door-surrounds'][i]!;
    mesh.frustumCulled = false;
    return mesh;
  });
  meshes[0]!.instanceMatrix.setUsage(DynamicDrawUsage);
  const bases = entrances.map((entry) =>
    new Matrix4()
      .makeBasis(
        new Vector3(entry.right[0], 0, -entry.right[1]),
        new Vector3(0, 1, 0),
        new Vector3(entry.outward[0], 0, -entry.outward[1])
      )
      .setPosition(
        entry.position[0] - (entry.right[0] * entry.width) / 2,
        entry.groundY,
        -entry.position[1] + (entry.right[1] * entry.width) / 2
      )
  );
  const angles = new Float64Array(entrances.length),
    matrix = new Matrix4(),
    turn = new Matrix4(),
    scale = new Vector3();
  entrances.forEach((entry, i) => {
    matrix.copy(bases[i]!).scale(scale.set(entry.width, entry.height, 1));
    for (const mesh of meshes) mesh.setMatrixAt(i, matrix);
  });
  for (const mesh of meshes) mesh.instanceMatrix.needsUpdate = true;
  let previous: number | undefined;
  return {
    objects: meshes,
    update(time: number, poses: readonly LifePose[]) {
      const dt = previous === undefined ? 0 : Math.max(0, Math.min(0.1, time - previous));
      previous = time;
      const open = new Set(poses.filter((pose) => pose.doorOpen).map((pose) => pose.door));
      let first = entrances.length,
        last = -1;
      entrances.forEach((entry, i) => {
        const target = open.has(entry.id) || ctx.crowdDoors?.has(entry.id) ? 1.2 : 0;
        const next = angles[i]! + Math.max(-dt * 3, Math.min(dt * 3, target - angles[i]!));
        if (next === angles[i]) return;
        angles[i] = next;
        matrix
          .copy(bases[i]!)
          .multiply(turn.makeRotationY(angles[i]))
          .scale(scale.set(entry.width, entry.height, 1));
        meshes[0]!.setMatrixAt(i, matrix);
        first = Math.min(first, i);
        last = i;
      });
      if (last >= first) {
        const attribute = meshes[0]!.instanceMatrix;
        /* One bounded upload avoids a separate driver call for every moving leaf. */
        attribute.clearUpdateRanges();
        attribute.addUpdateRange(first * 16, (last - first + 1) * 16);
        attribute.needsUpdate = true;
      }
    },
  };
}
